import {ScanRegionMaterialFragment} from "@platform/models/scanning/scanning-canvas-toolbox/tool-items/scan-region.generated"
import {EditMode, RectangleRegion} from "@common/helpers/canvas/canvas-base-toolbox-legacy/std-toolbox-items/rectangle-region"
import * as paper from "paper"
import {ResolutionOption, SampleRotation, ScanRegionInfo, ScanRegionParameters} from "@platform/models/scanning/types"
import {CanvasBaseToolboxItem} from "@common/helpers/canvas/canvas-base-toolbox-legacy/canvas-base-toolbox-item"
import {takeUntil} from "rxjs"

export type QueryMaterialForScanRegion = (materialLegacyId: number) => Promise<ScanRegionMaterialFragment>

export class ScanRegion extends RectangleRegion {
    sampleRotation: SampleRotation = 0
    megapixelWarningThreshold = 100

    private _materialId: number | null = null // we keep the material-id separate since we may need it before the material has actually loaded
    private _material: ScanRegionMaterialFragment | null = null
    private _resolution?: ResolutionOption
    private _state?: ScanRegionParameters["state"]
    private _transparent?: boolean
    private centerLabel?: paper.PointText
    private tileGroup?: paper.Group

    constructor(
        parent: CanvasBaseToolboxItem,
        x = 0,
        y = 0,
        width = 500,
        height = 500,
        private queryMaterialForScanRegion: QueryMaterialForScanRegion,
    ) {
        super(parent, x, y, width, height)
        this.physicalInfoChange.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
            this.updatePixelAreaFromPhysicalArea()
            this.updateTiles()
            // note: updateLabels() is already called by the physicalInfoChange subscription in the base class
        })
    }

    allowEdit = true

    set materialId(value: number | null) {
        this._materialId = value
        this._material = null
        if (!value) return

        this.queryMaterialForScanRegion(value).then((material) => {
            this._material = material
            this.updateLabels()
        })
    }

    get materialId() {
        return this._materialId
    }

    get material(): ScanRegionMaterialFragment | null {
        return this._material
    }

    /** Set materialId and material together, bypassing the async fetch. Used when material data is already available. */
    setMaterialFromCache(materialId: number, material: ScanRegionMaterialFragment): void {
        this._materialId = materialId
        this._material = material
        this.updateLabels()
    }

    get resolution(): ResolutionOption | undefined {
        return this._resolution
    }

    set resolution(value: ResolutionOption | undefined) {
        this._resolution = value
        this.updateLabels()
    }

    get state(): ScanRegionParameters["state"] | undefined {
        return this._state
    }

    set state(value: ScanRegionParameters["state"]) {
        this._state = value
        this.updateLabels()
    }

    get transparent(): boolean | undefined {
        return this._transparent
    }

    set transparent(value: boolean | undefined) {
        this._transparent = value
        this.updateLabels()
    }

    private _physicalArea: [number, number, number, number] = [0, 0, 1, 1]

    get area(): [number, number, number, number] {
        const pxPerCm = this.physicalInfo?.pixelsPerCm ?? 0
        if (pxPerCm === 0) {
            this.logger.warn(
                `ScanRegion area requested but physicalInfo is not set (physicalArea: ${this._physicalArea}, canvasRect: ${this.getRect()}, physicalInfo: ${this.physicalInfo})`,
            )
        } else {
            // update the physical area based on the current pixel area and physicalInfo
            const [x1, y1, x2, y2] = this.getRect()
            const cmPerPx = 1 / pxPerCm
            const physX = x1 * cmPerPx + this.physicalInfo!.originXCm
            const physY = y1 * cmPerPx + this.physicalInfo!.originYCm
            let physWidth = (x2 - x1) * cmPerPx
            let physHeight = (y2 - y1) * cmPerPx
            if (physWidth <= 0 || physHeight <= 0) {
                this.logger.warn(
                    `ScanRegion has non-positive physical dimensions (physicalArea: ${this._physicalArea}, canvasRect: ${this.getRect()}, physicalInfo: ${this.physicalInfo})`,
                )
                physWidth = physHeight = 1
            }
            this._physicalArea = [physX, physY, physWidth, physHeight]
            return this._physicalArea
        }
        return this._physicalArea
    }

    private updatePixelAreaFromPhysicalArea(): void {
        const pxPerCm = this.physicalInfo?.pixelsPerCm ?? 0
        if (pxPerCm === 0) {
            this.logger.warn(
                `ScanRegion area set but physicalInfo is not set (physicalArea: ${this._physicalArea}, canvasRect: ${this.getRect()}, physicalInfo: ${this.physicalInfo})`,
            )
        } else {
            const [physX, physY, physWidth, physHeight] = this._physicalArea
            if (physWidth <= 0 || physHeight <= 0) {
                this.logger.warn(
                    `ScanRegion area set with non-positive physical dimensions (physicalArea: ${this._physicalArea}, canvasRect: ${this.getRect()}, physicalInfo: ${this.physicalInfo})`,
                )
            }
            const pxX1 = (physX - this.physicalInfo!.originXCm) * pxPerCm
            const pxY1 = (physY - this.physicalInfo!.originYCm) * pxPerCm
            const pxX2 = pxX1 + physWidth * pxPerCm
            const pxY2 = pxY1 + physHeight * pxPerCm
            this.setRect(pxX1, pxY1, pxX2, pxY2)
        }
    }

    set area(physicalArea: [number, number, number, number]) {
        this._physicalArea = physicalArea
        this.updatePixelAreaFromPhysicalArea()
    }

    private initCenterLabel(): void {
        this.centerLabel = new paper.PointText(new paper.Point(0, 0))
        this.centerLabel.fillColor = new paper.Color("whitesmoke")
        this.centerLabel.justification = "center"
        this.centerLabel.content = "State label"
        this.centerLabel.fontSize = this.baseFontSize
        this.centerLabel.fontFamily = "Roboto"
        this.labelGroup.addChild(this.centerLabel)
    }

    protected override updateLabels(): void {
        super.updateLabels()

        const [x1, y1, x2, y2] = this.getRect()
        const width = x2 - x1
        const height = y2 - y1
        const position: paper.Point = new paper.Point(x1 + width / 2, y1 + height / 2)

        let redOutlineAndText = false
        let fillColorName: string
        let labelText: string

        switch (this._state) {
            case "editing":
                {
                    fillColorName = "whitesmoke"
                    const megapixel = this.getScanAreMegapixel()
                    if (megapixel > this.megapixelWarningThreshold) {
                        labelText = `WARNING\n~${megapixel.toFixed(0)} MP`
                        redOutlineAndText = true
                    } else {
                        labelText = `~${megapixel.toFixed(0)} MP`
                    }
                }
                break
            case "waiting":
                fillColorName = "whitesmoke"
                labelText = "Waiting"
                break
            case "scanning":
                fillColorName = "orange"
                labelText = "Scanning"
                break
            case "complete":
                fillColorName = "green"
                labelText = "Complete"
                break
            case "failed":
                fillColorName = "red"
                labelText = "Failed"
                break
            case "aborted":
                fillColorName = "black"
                labelText = "Aborted"
                break
        }

        this.rectanglePath.fillColor = new paper.Color(fillColorName!)
        this.rectanglePath.fillColor.alpha = 0.15

        if (!this.centerLabel) {
            this.initCenterLabel()
        }
        this.centerLabel!.content = labelText!
        this.centerLabel!.fontWeight = "Bold"
        this.centerLabel!.strokeColor = this.fontStrokeColor

        if (redOutlineAndText) {
            this.centerLabel!.fillColor = new paper.Color("red")
        } else {
            this.centerLabel!.fillColor = new paper.Color("whitesmoke")
        }
        this.rectanglePath.strokeColor = this.centerLabel!.fillColor
        this.rectanglePath.strokeWidth = 2

        this.centerLabel!.fontSize = this.baseFontSize / this.zoomLevel
        this.centerLabel!.strokeWidth = this.baseFontStrokeWidth / this.zoomLevel
        this.centerLabel!.position = position
        this.centerLabel!.visible = this.visible && this.centerLabel!.bounds.width <= 0.9 * width && this.centerLabel!.bounds.height <= 0.9 * height
    }

    private getScanAreMegapixel(): number {
        if (!this._resolution) return 0
        const scanPixelsWidth = this.canvasToScanPixels(this.width)
        const scanPixelsHeight = this.canvasToScanPixels(this.height)
        return (scanPixelsWidth * scanPixelsHeight) / 10 ** 6
    }

    protected override getLabel(canvasPixels: number): string {
        const cm = canvasPixels / this.physicalInfo!.pixelsPerCm
        const scanPixels = this._resolution ? this.canvasToScanPixels(canvasPixels) : 0
        return `${cm.toFixed(2)} cm (${scanPixels.toFixed(0)} px)`
    }

    private canvasToScanPixels(canvasPixels: number): number {
        const cm = canvasPixels / this.physicalInfo!.pixelsPerCm
        return cm * 10 * this._resolution!.pixelsPerMm
    }

    private _regionInfo: ScanRegionInfo | null = null

    private updateTiles() {
        const tiles = this._regionInfo?.tiles
        this.tileGroup?.remove()
        this.tileGroup = new paper.Group()
        this.regionGroup.addChild(this.tileGroup)
        this.tileGroup.sendToBack()
        if (tiles) {
            for (const tile of tiles) {
                const x = (tile.area[0] - this.physicalInfo!.originXCm) * this.physicalInfo!.pixelsPerCm
                const y = (tile.area[1] - this.physicalInfo!.originYCm) * this.physicalInfo!.pixelsPerCm
                const w = tile.area[2] * this.physicalInfo!.pixelsPerCm
                const h = tile.area[3] * this.physicalInfo!.pixelsPerCm
                const tileRect = new paper.Path.Rectangle(new paper.Point([x, y]), new paper.Size([w, h]))
                let colorName: string
                switch (tile.state) {
                    default:
                    case "editing":
                        colorName = "whitesmoke"
                        break
                    case "waiting":
                        colorName = "whitesmoke"
                        break
                    case "scanning":
                        colorName = "orange"
                        break
                    case "complete":
                        colorName = "green"
                        break
                    case "failed":
                        colorName = "red"
                        break
                    case "aborted":
                        colorName = "black"
                        break
                }
                tileRect.strokeColor = new paper.Color("whitesmoke")
                tileRect.strokeColor.alpha = 0.3
                tileRect.fillColor = new paper.Color(colorName)
                tileRect.fillColor.alpha = 0.15
                this.tileGroup.addChild(tileRect)
            }
        }
    }

    updateInfo(info: ScanRegionInfo | null) {
        this._regionInfo = info
        this.updateTiles()
    }

    protected override getEditModeByHitResult(hitResult: paper.HitResult | null): EditMode {
        if (this.allowEdit) {
            return super.getEditModeByHitResult(hitResult)
        } else {
            return hitResult ? EditMode.Select : EditMode.None
        }
    }
}
